<script setup>
import { ref, computed } from 'vue';
import { router, useForm, usePage, Head } from '@inertiajs/vue3';
import AdminLayout from '@/Layouts/AdminLayout/AdminLayout.vue';

const props = defineProps({
    wallets: {
        type: Array,
        default: () => [],
    },
});

const page = usePage();

const flash = computed(() => page.props.flash ?? {});
const errors = computed(() => page.props.errors ?? {});

// ── Import form ──────────────────────────────────────────────────
const showImportForm = ref(false);
const importForm = useForm({
    name:        '',
    private_key: '',
    network:     'mainnet',
});

function submitImport() {
    importForm.post('/admin/bsc-hot-wallets', {
        preserveScroll: true,
        onSuccess: () => {
            importForm.reset();
            showImportForm.value = false;
        },
    });
}

// ── Actions ──────────────────────────────────────────────────────
function toggleActive(wallet) {
    router.patch(`/admin/bsc-hot-wallets/${wallet.id}/toggle-active`, {}, {
        preserveScroll: true,
    });
}

function refreshBalance(wallet) {
    router.post(`/admin/bsc-hot-wallets/${wallet.id}/refresh-balance`, { network: 'mainnet' }, {
        preserveScroll: true,
    });
}

function deleteWallet(wallet) {
    if (!confirm(`Remove hot wallet "${wallet.name}" (${wallet.address})? This cannot be undone.`)) return;
    router.delete(`/admin/bsc-hot-wallets/${wallet.id}`, { preserveScroll: true });
}

function shortAddr(addr) {
    if (!addr) return '';
    return addr.slice(0, 8) + '…' + addr.slice(-6);
}

function formatBalance(val) {
    if (val === null || val === undefined) return '—';
    return Number(val).toFixed(4);
}

function bscScanUrl(addr) {
    return 'https://bscscan.com/address/' + addr;
}
</script>

<template>
    <AdminLayout>
        <Head title="BSC Hot Wallets" />

        <div class="p-6 max-w-6xl mx-auto">

            <!-- Header -->
            <div class="flex items-center justify-between mb-6">
                <div>
                    <h1 class="text-2xl font-bold text-gray-900 dark:text-white">BSC Hot Wallets</h1>
                    <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        Manage BEP-20 USDT hot wallets used to send on-chain withdrawals.
                    </p>
                </div>
                <button
                    @click="showImportForm = !showImportForm"
                    class="inline-flex items-center gap-2 px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white text-sm font-semibold rounded-lg shadow transition"
                >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                    </svg>
                    Import Hot Wallet
                </button>
            </div>

            <!-- Flash messages -->
            <div v-if="flash.success" class="mb-4 p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-lg text-green-700 dark:text-green-300 text-sm">
                {{ flash.success }}
            </div>
            <div v-if="errors.error" class="mb-4 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg text-red-700 dark:text-red-300 text-sm">
                {{ errors.error }}
            </div>

            <!-- Import form -->
            <div v-if="showImportForm" class="mb-6 bg-white dark:bg-gray-800 rounded-xl shadow border border-yellow-200 dark:border-yellow-700 p-6">
                <h2 class="text-lg font-semibold text-gray-800 dark:text-white mb-4">Import Hot Wallet Private Key</h2>

                <div class="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-4 mb-5 text-sm text-amber-800 dark:text-amber-300">
                    <strong>Security note:</strong> The private key is encrypted with AES-256-CBC using your
                    <code>APP_KEY</code> before storage (Laravel <code>Crypt</code>). It is never exposed in logs
                    or API responses. Only import keys for dedicated hot wallets — never your personal wallet.
                </div>

                <form @submit.prevent="submitImport" class="space-y-4">
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Wallet Name</label>
                            <input
                                v-model="importForm.name"
                                type="text"
                                placeholder="e.g. BSC Hot Wallet #1"
                                class="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                            />
                            <p v-if="importForm.errors.name" class="mt-1 text-xs text-red-600">{{ importForm.errors.name }}</p>
                        </div>

                        <div>
                            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Network</label>
                            <select
                                v-model="importForm.network"
                                class="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                            >
                                <option value="mainnet">BSC Mainnet</option>
                                <option value="testnet">BSC Testnet</option>
                            </select>
                        </div>
                    </div>

                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Private Key (64 hex chars, 0x optional)</label>
                        <input
                            v-model="importForm.private_key"
                            type="password"
                            placeholder="0x... or raw 64-character hex"
                            autocomplete="off"
                            class="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm font-mono text-gray-900 dark:text-white focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                        />
                        <p v-if="importForm.errors.private_key" class="mt-1 text-xs text-red-600">{{ importForm.errors.private_key }}</p>
                    </div>

                    <div class="flex gap-3">
                        <button
                            type="submit"
                            :disabled="importForm.processing"
                            class="px-5 py-2 bg-yellow-500 hover:bg-yellow-600 disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition"
                        >
                            <span v-if="importForm.processing">Importing…</span>
                            <span v-else>Import &amp; Derive Address</span>
                        </button>
                        <button
                            type="button"
                            @click="showImportForm = false; importForm.reset()"
                            class="px-5 py-2 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-200 text-sm font-semibold rounded-lg transition"
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </div>

            <!-- Wallets table -->
            <div class="bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden">
                <div v-if="wallets.length === 0" class="py-16 text-center text-gray-400 dark:text-gray-500">
                    <svg class="w-12 h-12 mx-auto mb-3 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                            d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
                    </svg>
                    <p class="font-medium">No hot wallets yet.</p>
                    <p class="text-sm mt-1">Import a wallet private key to get started.</p>
                </div>

                <table v-else class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead class="bg-gray-50 dark:bg-gray-700/50">
                        <tr>
                            <th class="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Name / Address</th>
                            <th class="px-4 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">USDT Balance</th>
                            <th class="px-4 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">BNB Balance</th>
                            <th class="px-4 py-3 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                            <th class="px-4 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                        </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
                        <tr v-for="wallet in wallets" :key="wallet.id"
                            class="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition">
                            <!-- Name / Address -->
                            <td class="px-4 py-4">
                                <p class="text-sm font-semibold text-gray-900 dark:text-white">{{ wallet.name }}</p>
                                <div class="flex items-center gap-2 mt-0.5">
                                    <a
                                        :href="bscScanUrl(wallet.address)"
                                        target="_blank"
                                        class="text-xs text-yellow-600 dark:text-yellow-400 font-mono hover:underline"
                                        :title="wallet.address"
                                    >
                                        {{ shortAddr(wallet.address) }}
                                    </a>
                                    <svg class="w-3 h-3 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                                    </svg>
                                </div>
                                <p v-if="wallet.balance_updated_at" class="text-xs text-gray-400 mt-0.5">
                                    Updated {{ new Date(wallet.balance_updated_at).toLocaleString() }}
                                </p>
                            </td>

                            <!-- USDT -->
                            <td class="px-4 py-4 text-right">
                                <span class="text-sm font-semibold text-gray-800 dark:text-gray-200">
                                    {{ formatBalance(wallet.cached_usdt_balance) }}
                                </span>
                                <span class="text-xs text-gray-400 ml-1">USDT</span>
                            </td>

                            <!-- BNB -->
                            <td class="px-4 py-4 text-right">
                                <span class="text-sm font-semibold text-gray-800 dark:text-gray-200">
                                    {{ formatBalance(wallet.cached_bnb_balance) }}
                                </span>
                                <span class="text-xs text-gray-400 ml-1">BNB</span>
                            </td>

                            <!-- Status -->
                            <td class="px-4 py-4 text-center">
                                <span
                                    class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold"
                                    :class="wallet.is_active
                                        ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                                        : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'"
                                >
                                    <span
                                        class="w-1.5 h-1.5 rounded-full mr-1.5"
                                        :class="wallet.is_active ? 'bg-green-500' : 'bg-gray-400'"
                                    ></span>
                                    {{ wallet.is_active ? 'Active' : 'Inactive' }}
                                </span>
                            </td>

                            <!-- Actions -->
                            <td class="px-4 py-4">
                                <div class="flex items-center justify-end gap-2">
                                    <!-- Refresh balance -->
                                    <button
                                        @click="refreshBalance(wallet)"
                                        title="Refresh balance"
                                        class="p-1.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition"
                                    >
                                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                                        </svg>
                                    </button>

                                    <!-- Toggle active -->
                                    <button
                                        @click="toggleActive(wallet)"
                                        :title="wallet.is_active ? 'Deactivate' : 'Activate'"
                                        class="p-1.5 transition"
                                        :class="wallet.is_active
                                            ? 'text-green-500 hover:text-gray-400'
                                            : 'text-gray-400 hover:text-green-500'"
                                    >
                                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M5.636 5.636a9 9 0 1012.728 0M12 3v9"/>
                                        </svg>
                                    </button>

                                    <!-- Delete -->
                                    <button
                                        @click="deleteWallet(wallet)"
                                        title="Remove wallet"
                                        class="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition"
                                    >
                                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                                        </svg>
                                    </button>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Info panel -->
            <div class="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600 dark:text-gray-400">
                <div class="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                    <p class="font-semibold text-gray-800 dark:text-gray-200 mb-1">How it works</p>
                    <p>When an admin approves a BSC USDT withdrawal, the system automatically signs and broadcasts an ERC-20 <code>transfer()</code> from the active hot wallet to the user's BSC address.</p>
                </div>
                <div class="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                    <p class="font-semibold text-gray-800 dark:text-gray-200 mb-1">Only one active wallet</p>
                    <p>Only one hot wallet should be active at a time. The system picks the first active wallet found. Deactivate others before switching.</p>
                </div>
                <div class="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                    <p class="font-semibold text-gray-800 dark:text-gray-200 mb-1">Key security</p>
                    <p>Private keys are encrypted with <code>APP_KEY</code> (AES-256-CBC) and are never logged or returned via API. Keep your <code>APP_KEY</code> secure and backed up.</p>
                </div>
            </div>
        </div>
    </AdminLayout>
</template>
